import React from 'react';
import { DNABox, DNAButton, DNAButtonProps, DNAText, useOrientationChange } from '@alucio/lux-ui';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { useContent } from 'src/state/context/ContentProvider/ContentProvider.proxy';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import {
  getVisiblePresentationPageCounter,
  shouldHidePresentationPageCounter,
} from 'src/utils/hiddenSlides';

interface PresentationNavProps {
  disablePageCount?: boolean
}

type ButtonDefaultsVariants = Record<DeviceMode, DNAButtonProps>

const PresentationNav:React.FC<PresentationNavProps> = ({
  disablePageCount,
}) => {
  const { activePresentation, nextStepPage, prevStepPage } = useContent()
  const { deviceMode } = useAppSettings()
  const orientation = useOrientationChange()
  const enableHiddenSlides = useFeatureFlags('BEAC_7666_enable_hidden_slides');

  if (!activePresentation) return null

  const buttonDefaultsVariants: ButtonDefaultsVariants = {
    desktop: {
      appearance: 'outline',
      status: 'subtle',
      colorMode: 'dark',
    },
    tablet: {
      appearance: 'outline',
      status: 'subtle',
      size: orientation === 'LANDSCAPE' ? 'xl' : 'lg',
      colorMode: 'dark',
    },
  }

  const currentButtonDefaultsVariant = buttonDefaultsVariants[deviceMode]
  const { presentable, currentPresentablePage } = activePresentation;
  const currentPageNumber = currentPresentablePage.presentationPageNumber
  const pageCounter = enableHiddenSlides
    ? getVisiblePresentationPageCounter({
      pages: presentable.presentablePages,
      currentPresentationPageNumber: currentPageNumber,
    })
    : {
      currentPageNumber,
      totalPages: presentable.numberOfPages,
    };
  const hidePageCounter = shouldHidePresentationPageCounter({
    featureEnabled: enableHiddenSlides,
    pages: presentable.presentablePages,
    currentPresentationPageNumber: currentPageNumber,
  });

  return (
    <DNABox spacing="sm" alignY="center">
      <DNAButton
        {...currentButtonDefaultsVariant}
        testID="previous-slide-button"
        iconLeft="caret-left"
        onPress={prevStepPage}
      />
      {!disablePageCount && !hidePageCounter &&
        <DNAText colorMode="dark" testID="slide-of-number">
          {`${pageCounter.currentPageNumber} of ${pageCounter.totalPages}`}
        </DNAText>
      }
      <DNAButton
        {...currentButtonDefaultsVariant}
        testID="next-slide-button"
        iconLeft="caret-right"
        onPress={nextStepPage}
      />
    </DNABox>
  )
}

export default PresentationNav;
